import type { SuggestionKeyDownProps, SuggestionProps } from "@tiptap/suggestion";

export type SuggestionState<T> = {
  open: boolean;
  loading: boolean;
  items: T[];
  index: number;
  rect: { top: number; bottom: number; left: number } | null;
  select: (item: T) => void;
};

export type SuggestionStore<T> = {
  get(): SuggestionState<T>;
  set(patch: Partial<SuggestionState<T>>): void;
  subscribe(listener: () => void): () => void;
  move(delta: number): void;
  choose(): boolean;
};

export function createSuggestionStore<T>(): SuggestionStore<T> {
  let state: SuggestionState<T> = { open: false, loading: false, items: [], index: 0, rect: null, select: () => {} };
  const listeners = new Set<() => void>();
  const store: SuggestionStore<T> = {
    get: () => state,
    set(patch) {
      state = { ...state, ...patch };
      for (const l of listeners) l();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    move(delta) {
      const n = state.items.length;
      if (n > 0) store.set({ index: (state.index + delta + n) % n });
    },
    choose() {
      const item = state.items[state.index];
      if (item === undefined) return false;
      state.select(item);
      return true;
    },
  };
  return store;
}

function rectOf(props: SuggestionProps<unknown, unknown>) {
  const r = props.clientRect?.();
  return r ? { top: r.top, bottom: r.bottom, left: r.left } : null;
}

export function storeRenderer<T>(store: SuggestionStore<T>) {
  return () => ({
    onStart: (props: SuggestionProps<T, T>) =>
      store.set({
        open: true,
        loading: props.loading,
        items: props.items,
        index: 0,
        rect: rectOf(props as SuggestionProps<unknown, unknown>),
        select: (item) => props.command(item),
      }),
    onUpdate: (props: SuggestionProps<T, T>) =>
      store.set({
        loading: props.loading,
        items: props.items,
        index: Math.min(store.get().index, Math.max(props.items.length - 1, 0)),
        rect: rectOf(props as SuggestionProps<unknown, unknown>),
        select: (item) => props.command(item),
      }),
    onExit: () => store.set({ open: false, loading: false, items: [], index: 0, rect: null }),
    onKeyDown: ({ event }: SuggestionKeyDownProps) => {
      if (!store.get().open) return false;
      if (event.key === "ArrowDown") {
        store.move(1);
        return true;
      }
      if (event.key === "ArrowUp") {
        store.move(-1);
        return true;
      }
      if (event.key === "Enter" || event.key === "Tab") return store.choose();
      if (event.key === "Escape") {
        store.set({ open: false });
        return true;
      }
      return false;
    },
  });
}
